import cloneDeep from 'lodash/cloneDeep'
import each from 'lodash/each'
import defaultsDeep from 'lodash/defaultsDeep'
import findIndex from 'lodash/findIndex'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import { isHardFail, isNetworkError } from 'lib/action'
import actionTypes from 'constants/actionTypes'
import createTypes from 'constants/createTypes'
import editTypes from 'constants/editTypes'
import deleteTypes from 'constants/deleteTypes'

interface PendingMeta {
  pending?: boolean
  retry?: boolean
  isRetry?: boolean
  retries?: number
  receivedAt?: number
  previousAction?: PendingAction
  [key: string]: unknown
}

interface PendingPayload {
  id?: string
  ids?: string[]
  [key: string]: unknown
}

interface PendingError {
  response?: {
    status?: number
  }
}

interface PendingAction {
  id?: string
  type?: string
  types?: unknown
  payload?: PendingPayload
  meta?: PendingMeta
  error?: PendingError
  [key: string]: unknown
}

interface PendingState {
  actions: PendingAction[]
  retrying: boolean
  enabled: boolean
}

interface TypeTriplet {
  REQUEST: string
  RECEIVE: string
  FAIL: string
}

const createTypeArrays = createTypes
  .map((a: TypeTriplet) => [a.REQUEST, a.RECEIVE, a.FAIL])

const editTypeArrays = editTypes
  .map((a: TypeTriplet) => [a.REQUEST, a.RECEIVE, a.FAIL])

const deleteTypeArrays = deleteTypes
  .map((a: TypeTriplet) => [a.REQUEST, a.RECEIVE, a.FAIL])

const pendingActions = (state = {
  actions: [],
  retrying: false,
  enabled: true
} as PendingState, action: PendingAction): PendingState => {
  if (action.type === actionTypes.PENDING.RETRY.START) {
    const updated = cloneDeep(state)
    updated.retrying = true
    return updated
  }
  if (action.type === actionTypes.PENDING.RETRY.END) {
    const updated = cloneDeep(state)
    const ids = action.payload?.ids ?? []
    updated.actions = updated.actions.filter((a: PendingAction) => {
      return !ids.includes(a.id ?? '')
    })
    updated.retrying = false
    return updated
  }
  if (action.type === actionTypes.PENDING.RETRY.DISABLE) {
    return defaultsDeep(
      {
        enabled: false
      },
      state
    )
  }
  if (action.type === actionTypes.PENDING.RETRY.ENABLE) {
    return defaultsDeep(
      {
        enabled: true
      },
      state
    )
  }
  const prevAction = action?.meta?.previousAction
  if (isHardFail(action)) {
    const prevActionId = prevAction?.id
    if (!prevActionId) return state
    const updated = cloneDeep(state)
    updated.actions = updated.actions.filter((a: PendingAction) => {
      return a.id !== prevActionId
    })
    return updated
  }
  if (action.meta?.pending || (
    action.error &&
    prevAction?.meta?.retry
  )) {
    const pending = prevAction != null ? cloneDeep(prevAction) : cloneDeep(action)
    pending.meta ??= {}
    if (action.error?.response?.status && !isNetworkError(action)) {
      pending.meta.retries = (pending.meta.retries ?? 0) + 1
    }
    const updated = addPendingAction(state, pending)
    return updated
  }
  return state
}

const addPendingAction = (state: PendingState, action: PendingAction): PendingState => {
  const nextAction: PendingAction = cloneDeep(action)
  delete nextAction.meta?.pending
  delete nextAction.type
  nextAction.meta ??= {}
  nextAction.meta.isRetry = true
  let combinedAction: PendingAction | null = nextAction
  const updated = cloneDeep(state)

  let idx = findIndex(state.actions, (a: PendingAction) => a.id === nextAction?.id)
  const payloadId = nextAction.payload?.id
  if (payloadId) {
    idx = -1
    const relatedActions: PendingAction[] = []
    each(state.actions, (a: PendingAction, i: number) => {
      if (a.payload?.id === payloadId) {
        relatedActions.push(a)
        if (idx === -1) idx = i
      }
    })
    combinedAction = combineRelatedActions(nextAction, relatedActions)
  }
  if (idx > -1 && !combinedAction) {
    updated.actions = [
      ...updated.actions.slice(0, idx),
      ...updated.actions.slice(idx + 1)
    ]
    return updated
  }
  if (idx > -1) {
    updated.actions = [
      ...updated.actions.slice(0, idx),
      combinedAction as PendingAction,
      ...updated.actions.slice(idx + 1)
    ]
  } else {
    updated.actions.push(combinedAction as PendingAction)
  }
  return updated
}

const combineRelatedActions = (action: PendingAction, relatedActions: PendingAction[]): PendingAction | null => {
  relatedActions = relatedActions.sort((a: PendingAction, b: PendingAction) => {
    return (a.meta?.receivedAt ?? 0) - (b.meta?.receivedAt ?? 0)
  })
  const actions = [...relatedActions, action]
  return actions.reduce((combined: PendingAction | null, a: PendingAction) => {
    if (combined == null) return cloneDeep(a)
    return combineTwoActions(combined, a)
  }, null)
}

const combineTwoActions = (a: PendingAction, b: PendingAction): PendingAction | null => {
  a = cloneDeep(a)
  b = cloneDeep(b)
  a.meta ??= {}
  b.meta ??= {}
  a.meta.retries = b.meta.retries ?? a.meta.retries ?? 0
  if (isCreate(a)) {
    if (isCreate(b)) {
      return b
    }
    if (isEdit(b)) {
      a.payload = defaultsDeep(b.payload, a.payload)
      return a
    }
    if (isDelete(b)) {
      return null
    }
  }
  if (isEdit(a)) {
    if (isEdit(b)) {
      a.payload = defaultsDeep(b.payload, a.payload)
      return a
    }
    if (isDelete(b)) {
      return b
    }
  }
  if (isDelete(a)) {
    return a
  }
  return b
}

const isCreate = (action: PendingAction) => {
  return find(createTypeArrays, (t: string[]) => isEqual(t, action.types))
}
const isEdit = (action: PendingAction) => {
  return find(editTypeArrays, (t: string[]) => isEqual(t, action.types))
}
const isDelete = (action: PendingAction) => {
  return find(deleteTypeArrays, (t: string[]) => isEqual(t, action.types))
}

export default pendingActions
